import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import cn from "classnames";

import "./index.less";

const noop = () => {};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date, delta) =>
  new Date(date.getFullYear(), date.getMonth() + delta, 1);

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const formatMonthLabel = (date) => {
  return moment(date).format("MMMM YYYY");
};

const getMonthGrid = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const firstWeekday = first.getDay(); // 0 = Sunday
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = Array.from({ length: 42 }, (_, index) => {
    const dayNum = index - firstWeekday + 1;
    if (dayNum < 1 || dayNum > daysInMonth) return null;
    return new Date(year, month, dayNum);
  });

  return cells;
};

const trimTrailingEmptyWeeks = (cells) => {
  let result = cells;
  while (result.length > 28 && result.slice(-7).every((d) => d === null)) {
    result = result.slice(0, -7);
  }
  return result;
};

/**
 * CalendarPicker (Explore redesign)
 *
 * Minimal, design-driven 2-month calendar card matching the Explore Figma.
 */
const CalendarPicker = ({
  className = "",
  value,
  defaultValue = null,
  onChange = noop,
  initialMonth = null,
  mobile = false,
}) => {
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const selectedDate = isControlled ? value : uncontrolledValue;
  const momentLocale = moment.locale();
  const WEEKDAYS = useMemo(() => {
    const isEnglish = String(momentLocale || "")
      .toLowerCase()
      .startsWith("en");

    // Always render Sunday -> Saturday
    return Array.from({ length: 7 }, (_, dayIndex) => {
      const label = moment().day(dayIndex).format(isEnglish ? "ddd" : "dd");
      return isEnglish ? String(label).charAt(0).toUpperCase() : label;
    });
  }, [momentLocale]);

  const resolvedInitialMonth = useMemo(() => {
    if (initialMonth instanceof Date && !Number.isNaN(initialMonth.getTime())) {
      return startOfMonth(initialMonth);
    }
    if (selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())) {
      return startOfMonth(selectedDate);
    }
    return startOfMonth(new Date());
  }, [initialMonth]);

  const [viewMonth, setViewMonth] = useState(resolvedInitialMonth);

  useEffect(() => {
    setViewMonth(resolvedInitialMonth);
  }, [resolvedInitialMonth]);

  const monthA = viewMonth;
  const monthB = useMemo(() => addMonths(monthA, 1), [monthA]);

  const gridA = useMemo(() => getMonthGrid(monthA), [monthA]);
  const gridB = useMemo(() => getMonthGrid(monthB), [monthB]);
  const gridAMobile = useMemo(() => trimTrailingEmptyWeeks(gridA), [gridA]);

  const selectDate = (date) => {
    if (!date) return;
    if (!isControlled) setUncontrolledValue(date);
    onChange(date);
  };

  if (mobile) {
    return (
      <div
        className={cn(
          "ui-calendar-picker",
          "ui-calendar-picker--mobile",
          className,
          { "is-mobile": mobile }
        )}
        role="group"
        aria-label="Calendar"
      >
        <div
          className="ui-calendar-picker__month"
          aria-label={formatMonthLabel(monthA)}
        >
          <div className="ui-calendar-picker__header">
            <button
              type="button"
              className="ui-calendar-picker__nav ui-calendar-picker__nav--prev"
              aria-label="Previous month"
              onClick={() => setViewMonth((prev) => addMonths(prev, -1))}
            >
              <i
                className="icon icon-arrow-left ui-calendar-picker__navIcon"
                aria-hidden="true"
              />
            </button>
            <div className="ui-calendar-picker__monthLabel">
              {formatMonthLabel(monthA)}
            </div>
            <button
              type="button"
              className="ui-calendar-picker__nav ui-calendar-picker__nav--next"
              aria-label="Next month"
              onClick={() => setViewMonth((prev) => addMonths(prev, 1))}
            >
              <i
                className="icon icon-arrow-right ui-calendar-picker__navIcon"
                aria-hidden="true"
              />
            </button>
          </div>

          <div className="ui-calendar-picker__weekdays" aria-hidden="true">
            {WEEKDAYS.map((d, index) => (
              <div key={index} className="ui-calendar-picker__weekday">
                {d}
              </div>
            ))}
          </div>

          <div
            className="ui-calendar-picker__grid"
            role="grid"
            aria-label={formatMonthLabel(monthA)}
          >
            {gridAMobile.map((date, index) => {
              if (!date) {
                return (
                  <div
                    key={`a-empty-${index}`}
                    className="ui-calendar-picker__cell ui-calendar-picker__cell--empty"
                    aria-hidden="true"
                  />
                );
              }

              const selected = isSameDay(date, selectedDate);
              const isBefore = moment(date).isBefore(new Date(), "day");
              return (
                <div
                  key={date.toISOString()}
                  className="ui-calendar-picker__cell"
                  role="gridcell"
                >
                  <button
                    type="button"
                    className={cn(
                      "ui-calendar-picker__dayBtn",
                      { "is-selected": selected },
                      { "is-today": isSameDay(date, new Date()) },
                      { "is-before": isBefore }
                    )}
                    aria-pressed={selected}
                    onClick={() => selectDate(date)}
                  >
                    {date.getDate()}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`ui-calendar-picker ${className}`.trim()}
      role="group"
      aria-label="Calendar"
    >
      <div
        className="ui-calendar-picker__month"
        aria-label={formatMonthLabel(monthA)}
      >
        <div className="ui-calendar-picker__header">
          <button
            type="button"
            className="ui-calendar-picker__nav ui-calendar-picker__nav--prev"
            aria-label="Previous month"
            onClick={() => setViewMonth((prev) => addMonths(prev, -1))}
          >
            <i
              className="icon icon-arrow-left ui-calendar-picker__navIcon"
              aria-hidden="true"
            />
          </button>
          <div className="ui-calendar-picker__monthLabel">
            {formatMonthLabel(monthA)}
          </div>
        </div>

        <div className="ui-calendar-picker__weekdays" aria-hidden="true">
          {WEEKDAYS.map((d, index) => (
            <div key={index} className="ui-calendar-picker__weekday">
              {d}
            </div>
          ))}
        </div>

        <div
          className="ui-calendar-picker__grid"
          role="grid"
          aria-label={formatMonthLabel(monthA)}
        >
          {gridA.map((date, index) => {
            if (!date) {
              return (
                <div
                  key={`a-empty-${index}`}
                  className="ui-calendar-picker__cell ui-calendar-picker__cell--empty"
                  aria-hidden="true"
                />
              );
            }

            const selected = isSameDay(date, selectedDate);
            const isBefore = moment(date).isBefore(new Date(), "day");
            return (
              <div
                key={date.toISOString()}
                className="ui-calendar-picker__cell"
                role="gridcell"
              >
                <button
                  type="button"
                  className={cn(
                    "ui-calendar-picker__dayBtn",
                    { "is-before": isBefore },
                    { "is-selected": selected },
                    { "is-today": isSameDay(date, new Date()) }
                  )}
                  aria-pressed={selected}
                  onClick={() => selectDate(date)}
                >
                  {date.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className="ui-calendar-picker__month"
        aria-label={formatMonthLabel(monthB)}
      >
        <div className="ui-calendar-picker__header">
          <button
            type="button"
            className="ui-calendar-picker__nav ui-calendar-picker__nav--next"
            aria-label="Next month"
            onClick={() => setViewMonth((prev) => addMonths(prev, 1))}
          >
            <i
              className="icon icon-arrow-right ui-calendar-picker__navIcon"
              aria-hidden="true"
            />
          </button>
          <div className="ui-calendar-picker__monthLabel">
            {formatMonthLabel(monthB)}
          </div>
        </div>

        <div className="ui-calendar-picker__weekdays" aria-hidden="true">
          {WEEKDAYS.map((d, index) => (
            <div key={index} className="ui-calendar-picker__weekday">
              {d}
            </div>
          ))}
        </div>

        <div
          className="ui-calendar-picker__grid"
          role="grid"
          aria-label={formatMonthLabel(monthB)}
        >
          {gridB.map((date, index) => {
            if (!date) {
              return (
                <div
                  key={`b-empty-${index}`}
                  className="ui-calendar-picker__cell ui-calendar-picker__cell--empty"
                  aria-hidden="true"
                />
              );
            }

            const selected = isSameDay(date, selectedDate);
            const isBefore = moment(date).isBefore(new Date(), "day");
            return (
              <div
                key={date.toISOString()}
                className="ui-calendar-picker__cell"
                role="gridcell"
              >
                <button
                  type="button"
                  className={cn(
                    "ui-calendar-picker__dayBtn",
                    { "is-selected": selected },
                    { "is-today": isSameDay(date, new Date()) },
                    { "is-before": isBefore }
                  )}
                  aria-pressed={selected}
                  onClick={() => selectDate(date)}
                >
                  {date.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CalendarPicker;
